
import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Square, Loader2 } from "lucide-react";
import { v4 as uuidv4 } from 'uuid';
import { Attachment } from "./ChatMessages";
import { uploadAttachment } from "./AttachmentUploader";

interface AudioRecorderProps {
  onAudioRecorded: (attachment: Attachment) => void;
}

export const AudioRecorder: React.FC<AudioRecorderProps> = ({ onAudioRecorded }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsProcessing(true);
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const duration = (Date.now() - startTimeRef.current) / 1000; // duração em segundos
        
        // Criar um arquivo a partir do Blob de áudio
        const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
        
        try {
          const companyId = localStorage.getItem('currentCompanyId') || "";
          const uploadUrl = await uploadAttachment(audioFile, companyId);
          
          if (uploadUrl) {
            const audioAttachment: Attachment = {
              id: uuidv4(),
              type: "audio",
              url: uploadUrl,
              duration: duration,
              size: audioBlob.size,
              mimeType: audioBlob.type
            };
            
            onAudioRecorded(audioAttachment);
          }
        } catch (error) {
          console.error("Erro ao processar áudio:", error);
        } finally {
          // Limpar recursos
          stream.getTracks().forEach(track => track.stop());
          setIsProcessing(false);
        }
      };

      startTimeRef.current = Date.now();
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Erro ao iniciar gravação:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <div>
      {isProcessing ? (
        <Button variant="ghost" size="icon" disabled className="h-8 w-8 rounded-full">
          <Loader2 className="h-4 w-4 animate-spin" />
        </Button>
      ) : !isRecording ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={startRecording}
          className="h-8 w-8 rounded-full hover:bg-red-100 hover:text-red-500"
        >
          <Mic className="h-4 w-4" />
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          onClick={stopRecording}
          className="h-8 w-8 rounded-full bg-red-100 text-red-500 animate-pulse"
        >
          <Square className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};
